export const formatDate = (dateString: string) => {
    if (!dateString) return "";

    const normalizedDateStr = dateString.trim().replace(/\[.*\]$/, "").replace(" ", "T");
    const date = new Date(normalizedDateStr);

    if (isNaN(date.getTime())) return "";

    return new Intl.DateTimeFormat("fr-FR", {
        day: "numeric",
        month: "long",
        year: "numeric",
    }).format(date);
};

export const formatDateTime = (dateString: string) => {
    if (!dateString) return "";

    const normalizedDateStr = dateString.trim().replace(/\[.*\]$/, "").replace(" ", "T");
    const date = new Date(normalizedDateStr);

    if (isNaN(date.getTime())) return "";

    const datePart = new Intl.DateTimeFormat("fr-FR", {
        day: "numeric",
        month: "long",
        year: "numeric",
    }).format(date);

    const timePart = new Intl.DateTimeFormat("fr-FR", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    }).format(date).replace(":", "h");

    return `${datePart} à ${timePart}`;
};

export const formatSeniority = (dateTime: string) => {
    if (!dateTime) return "";

    let normalizedDateTime = dateTime;
    if (normalizedDateTime.includes(" ") && !normalizedDateTime.includes("T")) {
        normalizedDateTime = normalizedDateTime.replace(" ", "T");
    }

    const date = new Date(normalizedDateTime);
    const now = new Date();

    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    // Moins d'une heure : afficher en minutes
    if (diffMinutes < 60) {
        return `${diffMinutes} minute${diffMinutes > 1 ? "s" : ""}`;
    }

    // Moins d'un jour : afficher en heures
    if (diffHours < 24) {
        return `${diffHours} heure${diffHours > 1 ? "s" : ""}`;
    }

    // Moins d'une semaine : afficher en jours
    if (diffDays < 7) {
        return `${diffDays} jour${diffDays > 1 ? "s" : ""}`;
    }

    // Moins d'un mois : afficher en semaines
    if (diffDays < 30) {
        const weeks = Math.floor(diffDays / 7);
        return `${weeks} semaine${weeks > 1 ? "s" : ""}`;
    }

    // Plus d'un mois : afficher en années et mois
    const years = now.getFullYear() - date.getFullYear();
    let months = now.getMonth() - date.getMonth();

    if (now.getDate() < date.getDate()) {
        months -= 1;
    }

    if (months < 0) {
        months += 12;
    }

    if (years <= 0) {
        return `${months} mois`;
    }

    const yearLabel = `${years} an${years > 1 ? "s" : ""}`;
    const monthLabel = months > 0 ? ` et ${months} mois` : "";

    return `${yearLabel}${monthLabel}`;
};

export function formatReadingTime(minutes?: number): string {
    if (!minutes) {
        return "";
    }

    if (minutes < 60) {
        return `${minutes} min`;
    }

    if (minutes < 1440) {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours}h${mins > 0 ? ` ${mins}m` : ""}`;
    }

    const days = Math.floor(minutes / 1440);
    const remainingMinutes = minutes % 1440;
    const hours = Math.floor(remainingMinutes / 60);
    const mins = remainingMinutes % 60;

    const daysLabel = `${days} jour${days > 1 ? "s" : ""}`;
    const timeLabel = hours > 0 || mins > 0 ? ` ${hours}h${mins > 0 ? ` ${mins}m` : ""}` : "";

    return `${daysLabel}${timeLabel}`;
}

/**
 * Compare une date avec l'instant présent.
 * Gère les dates UTC (avec Z) et les dates locales (sans timezone).
 */
export const isFutureDate = (dateString: string | null | undefined): boolean => {
    if (!dateString) return false;

    // Si la date contient Z ou un offset, Date(string) l'interprète correctement comme UTC ou avec offset.
    // Si elle ne contient rien, elle est interprétée comme locale.
    // On retire l'éventuel suffixe de timezone IANA entre crochets (ex: [Europe/Paris])
    // qui n'est pas supporté par le constructeur Date standard.
    // On normalise aussi le format si c'est "YYYY-MM-DD HH:mm:ss" -> "YYYY-MM-DDTHH:mm:ss"
    const normalizedDateStr = dateString.trim().replace(/\[.*\]$/, "").replace(" ", "T");
    const targetDate = new Date(normalizedDateStr);

    if (isNaN(targetDate.getTime())) return false;

    return targetDate.getTime() > Date.now();
};

export function slug(str: string) {
    return str.replace(/\s+/g, "-").replace(/"/g, "").toLowerCase();
}

/**
 * Lightens a hex color by a given percentage.
 * @param hex The hex color string (e.g., "#RRGGBB" or "RRGGBB").
 * @param opacity The opacity to apply (0 to 1).
 * @returns An rgba color string.
 */
export function hexToRgba(hex: string, opacity: number): string {
    let r = 0, g = 0, b = 0;

    // Remove # if present
    hex = hex.replace(/^#/, "");

    if (hex.length === 3) {
        const r_hex = hex[0]!;
        const g_hex = hex[1]!;
        const b_hex = hex[2]!;
        r = parseInt(r_hex + r_hex, 16);
        g = parseInt(g_hex + g_hex, 16);
        b = parseInt(b_hex + b_hex, 16);
    } else if (hex.length === 6) {
        r = parseInt(hex.substring(0, 2), 16);
        g = parseInt(hex.substring(2, 4), 16);
        b = parseInt(hex.substring(4, 6), 16);
    }

    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/**
 * Darkens a hex color by a given percentage.
 * @param hex The hex color string.
 * @param percent The percentage to darken (0 to 100).
 * @returns A hex color string.
 */
export function darkenColor(hex: string, percent: number): string {
    hex = hex.replace(/^#/, "");
    if (hex.length === 3) {
        const r_hex = hex[0]!;
        const g_hex = hex[1]!;
        const b_hex = hex[2]!;
        hex = r_hex + r_hex + g_hex + g_hex + b_hex + b_hex;
    }

    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    const darken = (n: number) => Math.max(0, Math.min(255, Math.floor(n * (1 - percent / 100))));

    const rs = darken(r).toString(16).padStart(2, "0");
    const gs = darken(g).toString(16).padStart(2, "0");
    const bs = darken(b).toString(16).padStart(2, "0");

    return `#${rs}${gs}${bs}`;
}

export const pluralize = (count: number, singular: string, plural: string): string => {
    return count > 1 ? plural : singular;
};

export const nl2br = (str: string): string => {
    return str.replace(/\n/g, "<br />\n");
};
